import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { CountryCombobox } from '@/components/CountryCombobox';
import { DEFAULT_COUNTRY } from '@/lib/countries';

const NEW_CLUB_SENTINEL = '__new__';

const emptyNewClub = {
  name: '',
  short_name: '',
  abbreviation: '',
  competition_region: '',
  contact_email: '',
  offers_artistic: true,
  offers_figures: false,
};

export default function RegisterPage() {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [clubs, setClubs] = useState([]);
  const [clubChoice, setClubChoice] = useState('');
  const [newClub, setNewClub] = useState(emptyNewClub);
  const [country, setCountry] = useState(DEFAULT_COUNTRY);
  const [countryTouched, setCountryTouched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pendingMessage, setPendingMessage] = useState(null);
  const navigate = useNavigate();

  // Fetch clubs on mount — only actual "Club" org type is offered.
  useEffect(() => {
    axios.get(`${API}/public/clubs`)
      .then(r => setClubs(r.data || []))
      .catch(() => {});
  }, []);

  // Whenever the user picks a club, sync the country default from that
  // club (unless they've already manually tweaked the combobox).
  useEffect(() => {
    if (countryTouched || !clubChoice || clubChoice === NEW_CLUB_SENTINEL) return;
    const chosen = clubs.find(c => c.club_id === clubChoice);
    if (chosen?.country) setCountry(chosen.country);
  }, [clubChoice, clubs, countryTouched]);

  const wantsNewClub = clubChoice === NEW_CLUB_SENTINEL;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!clubChoice) {
      toast.error('Please select your club (or request a new one).');
      return;
    }

    if (wantsNewClub) {
      const missing = ['name', 'short_name', 'abbreviation', 'competition_region', 'contact_email']
        .filter(k => !newClub[k]?.trim());
      if (missing.length) {
        toast.error('Please complete every new-club field.');
        return;
      }
      if (!newClub.offers_artistic && !newClub.offers_figures) {
        toast.error('New club must offer artistic and/or figures.');
        return;
      }
    }

    const payload = {
      first_name: firstName,
      last_name: lastName,
      email,
      password,
      country,
    };
    if (wantsNewClub) {
      payload.new_club = newClub;
    } else {
      payload.club_id = clubChoice;
    }

    setLoading(true);
    try {
      const r = await axios.post(`${API}/auth/register`, payload, { withCredentials: true });
      // Email verification (Feb 2026): backend no longer auto-signs the
      // user in — every new account must verify first.
      if (r.data?.requires_verification) {
        const clubMsg = r.data?.account_status === 'pending_club_approval'
          ? " Your club request is also pending super-user approval; you'll be notified when that's approved."
          : "";
        setPendingMessage(
          `We've sent a verification link to ${email}. Please open your inbox and click the link to activate your account before signing in.${clubMsg}`
        );
        return;
      }
      // Legacy fallback (shouldn't fire now that /register always
      // returns requires_verification, but keep for graceful behaviour):
      if (r.data?.account_status === 'pending_club_approval') {
        setPendingMessage(
          "Your account has been created pending confirmation of your club details. " +
          "You'll receive a notification once that's been approved."
        );
        return;
      }
      toast.success('Registration successful!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignup = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  if (pendingMessage) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: '#FAF9F6' }}>
        <Card className="w-full max-w-md p-8 shadow-lg text-center" data-testid="pending-club-approval-message">
          <div className="text-5xl mb-4">⏳</div>
          <h1 className="text-2xl font-bold mb-3" style={{ fontFamily: 'Manrope, sans-serif', color: '#2D4A3E' }}>
            Almost there!
          </h1>
          <p className="text-muted-foreground mb-6">{pendingMessage}</p>
          <Link to="/login" className="font-medium" style={{ color: '#2D4A3E' }}>
            Back to sign-in
          </Link>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-8" style={{ backgroundColor: '#FAF9F6' }}>
      <Card className="w-full max-w-md p-8 shadow-lg">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif', color: '#2D4A3E' }}>
            Get Started
          </h1>
          <p className="text-muted-foreground">Create your CompCreator account</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="firstName">First Name</Label>
              <Input id="firstName" type="text" value={firstName}
                     onChange={(e) => setFirstName(e.target.value)}
                     data-testid="firstname-input" required className="input-custom mt-1" />
            </div>
            <div>
              <Label htmlFor="lastName">Last Name</Label>
              <Input id="lastName" type="text" value={lastName}
                     onChange={(e) => setLastName(e.target.value)}
                     data-testid="lastname-input" required className="input-custom mt-1" />
            </div>
          </div>

          <div>
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" value={email}
                   onChange={(e) => setEmail(e.target.value)}
                   data-testid="email-input" required className="input-custom mt-1" />
          </div>

          <div>
            <Label htmlFor="password">Password</Label>
            <Input id="password" type="password" value={password}
                   onChange={(e) => setPassword(e.target.value)}
                   data-testid="password-input" required className="input-custom mt-1"
                   minLength={10} />
            <p className="text-xs text-muted-foreground mt-1">
              Minimum 10 characters with 3 of: lower/upper/digit/symbol.
            </p>
          </div>

          <div>
            <Label htmlFor="club">Your Club *</Label>
            <select id="club" required value={clubChoice}
                    onChange={(e) => setClubChoice(e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 bg-white mt-1"
                    data-testid="club-select">
              <option value="">-- Select your club --</option>
              {clubs.map(c => (
                <option key={c.club_id} value={c.club_id}>{c.name}</option>
              ))}
              <option value={NEW_CLUB_SENTINEL}>+ My club isn&apos;t listed — request a new one</option>
            </select>
          </div>

          <div>
            <Label>Country *</Label>
            <div className="mt-1">
              <CountryCombobox
                value={country}
                onChange={(v) => { setCountry(v); setCountryTouched(true); }}
                testId="register-country"
              />
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Defaults to your club&apos;s country — change it if you&apos;re registering from elsewhere.
            </p>
          </div>

          {wantsNewClub && (
            <div className="border border-amber-200 bg-amber-50 rounded-md p-4 space-y-3"
                 data-testid="new-club-panel">
              <p className="text-sm text-amber-900">
                <strong>New club request</strong> — your account will be created pending
                super-user approval of this club.
              </p>
              <div>
                <Label>Club Name *</Label>
                <Input value={newClub.name}
                       onChange={(e) => setNewClub({ ...newClub, name: e.target.value })}
                       data-testid="newclub-name" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Short Name *</Label>
                  <Input value={newClub.short_name}
                         onChange={(e) => setNewClub({ ...newClub, short_name: e.target.value })}
                         data-testid="newclub-shortname" />
                </div>
                <div>
                  <Label>Abbreviation * (≤5)</Label>
                  <Input value={newClub.abbreviation} maxLength={5}
                         onChange={(e) => setNewClub({ ...newClub, abbreviation: e.target.value.slice(0, 5) })}
                         data-testid="newclub-abbrev" />
                </div>
              </div>
              <div>
                <Label>Competition Region *</Label>
                <Input value={newClub.competition_region}
                       onChange={(e) => setNewClub({ ...newClub, competition_region: e.target.value })}
                       placeholder="e.g. Auckland-Northland"
                       data-testid="newclub-region" />
              </div>
              <div>
                <Label>Club Contact Email *</Label>
                <Input type="email" value={newClub.contact_email}
                       onChange={(e) => setNewClub({ ...newClub, contact_email: e.target.value })}
                       data-testid="newclub-email" />
              </div>
              <div className="flex gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox checked={newClub.offers_artistic}
                            onCheckedChange={(val) => setNewClub({ ...newClub, offers_artistic: !!val })}
                            data-testid="newclub-artistic" />
                  <span className="text-sm">Artistic</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox checked={newClub.offers_figures}
                            onCheckedChange={(val) => setNewClub({ ...newClub, offers_figures: !!val })}
                            data-testid="newclub-figures" />
                  <span className="text-sm">Figures</span>
                </label>
              </div>
            </div>
          )}

          <Button type="submit" className="w-full btn-primary" data-testid="register-button" disabled={loading}>
            {loading ? 'Creating account...' : 'Create Account'}
          </Button>
        </form>

        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-stone-200" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white text-muted-foreground">Or continue with</span>
          </div>
        </div>

        <Button type="button" variant="outline" className="w-full"
                onClick={handleGoogleSignup} data-testid="google-signup-button">
          <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Sign up with Google
        </Button>

        <p className="text-center text-sm text-muted-foreground mt-6">
          Already have an account?{' '}
          <Link to="/login" className="font-medium" style={{ color: '#2D4A3E' }}>Sign in</Link>
        </p>
      </Card>
    </div>
  );
}
